import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountingService } from '../../accounting.service';
import { PaginationService } from '../../../../helpers/pagination.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { CommonService } from '../../../../helpers/common.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  IInvoiceByIdResponseObject,
  IInvoiceLogData,
  IInvoiceLogDataResponse,
  Invoice,
} from '../../models/invoice.model';

@Component({
  selector: 'app-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: [
    './invoice-details.component.scss',
    '../../accounting-table.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InvoiceDetailsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  queryParams: any;
  invoiceForm: FormGroup = new FormGroup({
    eolContactId: new FormControl('', Validators.required),
  });
  financeMutationId = 0;
  invoiceData: any;
  isEolContactIdEditable = false;

  pageLimit = 1000;
  currentPageSize = 20;
  currentPageIndex = 1;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  totalRecords: number = 0;
  dataSource = new MatTableDataSource<IInvoiceLogData>([]);
  invoiceLogData: IInvoiceLogData[] = []; // To store invoice logs data
  displayedColumns = ['date', 'name', 'description'];
  status: string = '';
  private dataSubscription: Subscription | undefined;
  private routeParamSubscription: Subscription | undefined;
  invoiceId: number = 0;
  showLogForm = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paginationService: PaginationService,
    private _commonService: CommonService,
    private _snackBar: MatSnackBar,
    private _accountingService: AccountingService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.financeMutationId = Number(id);
        this.getFinanceMutationById();
        this.getInvoiceLogsData();
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  getInvoiceDetails() {
    this.dataSubscription = this._accountingService.invoiceDataById$.subscribe(
      (data: any) => {
        if (data) {
          this.invoiceData = data;
          this.invoiceId = this.invoiceData.invoiceId;
          this.status = this.getInvoiceStatus();
          this.invoiceForm.controls['eolContactId'].setValue(
            this.invoiceData.eolContactId,
          );
        }
      },
    );
  }

  getFinanceMutationById() {
    this._accountingService.getInvoiceById(this.financeMutationId).subscribe({
      next: (res: IInvoiceByIdResponseObject) => {
        this.invoiceData = res.value;
      },
      error: (error: Error) => {
        console.log(error);
      },
    });
  }

  editEolContactId() {
    this.isEolContactIdEditable = !this.isEolContactIdEditable;
    this.invoiceForm.controls['eolContactId'].getRawValue();
  }

  getInvoiceStatus() {
    const statusMapping = this._commonService.statusToInvoiceMapping;
    const statusKeys = Object.keys(statusMapping);
    const statusValues = Object.values(statusMapping);
    const statusIndex = statusValues.indexOf(
      parseInt(this.invoiceData.importStatus),
    );
    return (this.status = statusIndex !== -1 ? statusKeys[statusIndex] : '');
  }

  updateLogs() {
    this.showLogForm = !this.showLogForm;
  }

  saveEolContactId() {
    if (this.invoiceForm.valid) {
      const eolContactId =
        this.invoiceForm.controls['eolContactId']?.getRawValue();
      this.invoiceForm.controls['eolContactId'].setValue(eolContactId);
      this._accountingService
        .saveEolContactId(this.invoiceData.id, eolContactId)
        .subscribe({
          next: (response) => {
            console.log(response);
            this._snackBar.open(
              'Saved invoices details successfully',
              'Close',
              {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
              },
            );
            this.getFinanceMutationById();
            this.cdr.detectChanges();
          },
          error: () => {
            this._snackBar.open('Failed to fetch invoices logs', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          },
        });
      this.invoiceForm.controls['eolContactId'].setValue(eolContactId);
      this.invoiceData = {
        ...this.invoiceData,
        eolContactId: eolContactId,
      };
      this.invoiceForm.patchValue({ eolContactId: eolContactId });
      this.isEolContactIdEditable = false;
    }
  }

  cancelWolContactId() {
    this.isEolContactIdEditable = false;
    this.invoiceForm.controls['eolContactId'].setValue(
      this.invoiceData.eolContactId,
    );
  }

  getInvoiceLogsData() {
    if (this.financeMutationId) {
      this._accountingService
        .getInvoiceLogs(
          this.financeMutationId,
          this.currentPageIndex,
          this.currentPageSize,
        )
        .subscribe({
          next: (data: IInvoiceLogDataResponse) => {
            if (!data.isError) {
              const logData = data.value.financeLogLines;
              this.dataSource.data = logData;
              this.totalRecords = data.value.totalRecords;

              // show table view
              this.showLogForm = false;

              // Ensure paginator/sort attach AFTER DOM loads
              setTimeout(() => {
                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;
              });
              // this.dataSource.data = [...this.dataSource.data, ...logData];
              // this.invoiceLogData = this.dataSource.data.slice(
              //   0,
              //   this.currentPageSize,
              // );
              // this.showLogForm = false;
              // this.totalRecords = data.value.totalRecords;
              // this.paginator.length = this.totalRecords; // Set paginator length
            } else {
              this._snackBar.open(`Error while fetching log data`, 'Close', {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
              });
            }
          },
          error: () => {
            this._snackBar.open('Failed to fetch invoices logs', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          },
        });
    } else {
      this._snackBar.open('No invoice ID available to fetch logs', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    }
  }

  handlePageEvent(event: any) {
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.getInvoiceLogsData();
  }

  getLogData(data: any): void {
    const body = {
      id: this.financeMutationId,
      taskId: this.invoiceData.taskId,
      financeMutationId: this.financeMutationId,
      comment: data.comment,
    };
    this._accountingService.updateInvoiceLogData(body).subscribe({
      next: (data: any) => {
        if (!data.isError) {
          this._snackBar.open('Successfully updated invoices logs', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
          this.dataSource.data = [];
          this.showLogForm = !this.showLogForm;
          this.cdr.detectChanges();
          this.getInvoiceLogsData();
        } else {
          this._snackBar.open(`${data.value.value}`, 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
      },
      error: () => {
        this._snackBar.open('Error in fetching Invoice logs', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  goToInvoicePage() {
    this.router.navigate(['/klusup/accounting/invoice']);
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.routeParamSubscription) {
      this.routeParamSubscription.unsubscribe();
    }
  }

  importData(args0: any) {
    console.log(args0);
  }
}
